import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, Users, BookOpen, ExternalLink, DollarSign } from "lucide-react";

export default function AuthorEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/author/earnings").then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load earnings"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="kb-mono text-xs text-kb-ink-muted">Loading earnings…</div>;
  if (!data) return <div className="text-sm text-kb-ink-muted">No data.</div>;

  return (
    <div data-testid="author-earnings-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CREATOR EARNINGS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Your <em className="italic text-kb-primary">course revenue</em>
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        You earn <strong className="text-kb-ink">{data.author_share_pct}%</strong> of every paid enrolment in courses you author. KhojByte keeps {data.platform_share_pct}% for platform operations and partner-discount subsidies.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-summary">
        {[
          { icon: Wallet, label: "Total earned", value: `NPR ${data.total_earned_npr.toLocaleString()}`, color: "text-emerald-700" },
          { icon: TrendingUp, label: "Gross revenue", value: `NPR ${data.total_gross_npr.toLocaleString()}`, color: "text-kb-primary" },
          { icon: Users, label: "Paid enrolments", value: data.total_paid_enrollments, color: "text-kb-ink" },
          { icon: BookOpen, label: "Courses live", value: data.courses?.length || 0, color: "text-kb-ink" },
        ].map((s, i) => (
          <div key={i} className="kb-card p-5">
            <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-3">{s.label}</div>
            <div className={`font-serif text-2xl mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-2xl text-kb-ink">By course</h2>
        <div className="mt-4 kb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                <th className="px-4 py-3 text-left">Course</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Paid enrol.</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Your earnings</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.courses?.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-kb-ink-muted">No courses yet. <Link to="/dashboard/courses/new/edit" className="text-kb-primary font-medium">Author your first course →</Link></td></tr>
              )}
              {data.courses?.map((c) => (
                <tr key={c.id} className="border-b border-slate-200 last:border-0" data-testid={`earnings-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/dashboard/courses/${c.id}/edit`} className="text-kb-ink hover:text-kb-primary font-medium">{c.title}</Link>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">{c.status}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">NPR {(c.price_npr || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.paid_enrollments}</td>
                  <td className="px-4 py-3 text-right font-mono text-kb-ink-muted">NPR {c.gross_npr.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700 font-medium">NPR {c.earned_npr.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/dashboard/courses/${c.id}`} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.recent_payments?.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-kb-ink">Recent paid enrolments</h2>
          <div className="mt-4 kb-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Provider</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">Your share</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_payments.map((p) => (
                  <tr key={p.payment_id} className="border-b border-slate-200 last:border-0">
                    <td className="px-4 py-3 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                      {p.completed_at ? new Date(p.completed_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">{p.course_title}</td>
                    <td className="px-4 py-3 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{p.provider}</td>
                    <td className="px-4 py-3 text-right font-mono">NPR {(p.amount_npr || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-700">NPR {(p.earned_npr || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-10 kb-card p-5 bg-kb-subtle border-kb-primary/20">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-kb-primary shrink-0" strokeWidth={1.5} />
          <div className="text-sm text-kb-ink">
            <strong>Monthly payouts</strong> — Earnings are settled at month-end via eSewa/Khalti to the bank/wallet on file. Need to update payout details? Visit your{" "}
            <Link to="/dashboard/portfolio" className="text-kb-primary font-medium">portfolio settings</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}
